// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { TaskOutput, TaskStatus, OrgPermissionsEnum, ObjectTypeV1 as ObjectType } from 'thehive-sdk';
import { useCurrentUser } from '@Queries/userQueries';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import TaskListTitle from '@Organisms/Task/TaskListTitle/TaskListTitle';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import RouteBuilder from '@Services/RouteBuilder';
import client from '@Client';
import { RouteType } from '@Types';
import { actionList } from '@Organisms/Task/actionList';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { IsFlag } from '@Organisms/IsFlag/IsFlag';
import FormatMultipleDate from '@Molecules/Format/FormatMultipleDate/FormatMultipleDate';
import useConfigList from '@Hooks/useConfigList';
import { computeEventList } from '@Organisms/Task/eventList';
import { useGenericCountQuery } from '@Queries/genericQueries';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import QuickFilterList from '@Molecules/QuickFilterList/QuickFilterList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { useListAllFields } from '@Queries/describeQueries';
import { usePin } from '@Molecules/Pin';
import { ICONS } from '@Constants/iconsApp';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { ColumnType } from 'antd/lib/table';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { computeListSelector } from '@Organisms/GlobalTasks/selector';
import CaseNumber from '@Organisms/Case/CaseNumber/CaseNumber';
import { Link, useHistory } from 'react-router-dom';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import IconCount from '@Atoms/IconCount/IconCount';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { useDrawer } from '@Hooks/useDrawer';
import TaskDrawer from '@Organisms/Task/TaskDrawer/TaskDrawer';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { IsActionRequired } from '@Organisms/IsActionRequired/IsActionRequired';
import { IsMandatory } from '@Organisms/IsMandatory/IsMandatory';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';

import './GlobalTaskList.scss';

const extractPermissions = (task: TaskOutput) => task?.extraData?.case?.userPermissions || [];

const GlobalTaskList = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const pinContext = usePin();
    const { data: describeData } = useListAllFields();

    const config = useConfigList<TaskOutput>({
        selector: computeListSelector({ describeData: describeData as any }),
        controller: client.TaskController,
        storageId: StorageFilterEnum.GLOBAL_TASKS,
        objectType: ObjectType.TASK,
    });

    const { visible, setVisible, data, setData } = useDrawer<TaskOutput>({});

    const { data: currentUser } = useCurrentUser();

    const { login = '' } = currentUser || {};

    const {
        listRefetch,
        dataSource = [],
        columnsRenderer,
        status = 'loading',
        countRefetch,
        quickFilterListRenderer,
        stateSelectedKeys: { selectedKeys },
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        storagePageManager,
        storageViewManager,
        filterManager,
        paginationManager,
        table,
        queryParams,
    } = config;

    const {
        page: storagePage,
        setDisplay,
        setListAsGroup,
        setFilterIsOpen,
        setAutoRefresh,
        setOnLoadView,
    } = storagePageManager || { page: null };

    const { setDefaultDisplay } = storageViewManager || {};

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        startDate: { label: i('common.start-date'), visible: storagePage?.display?.startDate ?? true },
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh ?? false);
    const [localListAsGroup, setlocalListAsGroup] = useState(storagePage?.listAsGroup ?? false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);
    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
        setlocalListAsGroup(viewContent?.listAsGroup || false);
        loadVisibleStateDates({
            startDate: viewContent?.display?.startDate ?? true,
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.TaskController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    listRefetch();
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                },
            }),
        [listRefetch, countRefetch, i],
    );

    const getColumns = (disableSort = false): ColumnType<TaskOutput>[] =>
        columnsRenderer([
            {
                className: 'global-task-list__column-status',
                render: (
                    { flag = false, mandatory = false, extraData },
                    { flag: filterFlag, custom: filterCustom },
                ): ReactElement => {
                    const { actionRequired = false } = extraData || {};
                    return (
                        <div className="global-task-list____column-status-state">
                            <IsFlag value={flag} onClick={(): void => filterFlag(flag)} />
                            <IsActionRequired value={actionRequired} />
                            <IsMandatory
                                value={mandatory}
                                onClick={(): void => filterCustom({ field: 'mandatory', operator: `${mandatory}` })}
                            />
                        </div>
                    );
                },
            },
            {
                title: [
                    { label: i('common.group'), sortField: 'group', disableSort },
                    { label: i('task.list.table.columns.task'), sortField: 'title', disableSort },
                ],
                className: 'global-task-list__column-title column_preview',
                render: (task, { date: filterDate, custom: filterCustom, status: filterStatus }): ReactElement => (
                    <TaskListTitle
                        task={task}
                        caseId={task.extraData?.case?._id || ''}
                        onDueDateClick={(dueDate) => filterDate(dueDate, 'dueDate')}
                        onGroupClick={(group) => filterCustom({ field: 'group', values: [group], operator: 'any' })}
                        onStatusClick={(status) => filterStatus(status)}
                        showCaseInfo
                        onPreviewClick={() => {
                            setVisible(true);
                            setData(task);
                        }}
                    />
                ),
            },
            {
                title: i('common.case'),
                className: 'global-task-list__column-case',
                render: ({ extraData }): ReactElement => (
                    <span className="global-task-list__case">
                        <CaseNumber
                            hashtag
                            severity={extraData?.case?.severity}
                            caseNumber={extraData?.case?.number || 0}
                        />
                        <Link
                            className="global-task-list__case__link"
                            to={RouteBuilder.compute('cases.tasks', { caseId: extraData?.case?._id })}
                        >
                            {extraData?.case?.title}
                        </Link>
                    </span>
                ),
            },
            {
                title: [{ label: i('task.list.table.columns.assignee'), sortField: 'assignee', disableSort }],
                className: 'global-task-list__column-assignee',
                render: ({ assignee }, { assignee: filterAssignee }): ReactElement => (
                    <>{assignee && <AssigneeInfo onClick={filterAssignee} assignee={assignee} avatarOnly />}</>
                ),
            },
            {
                title: [
                    { label: i('common.dates') },
                    { label: 'S.', sortField: 'startDate', disableSort, hide: !showDates.startDate },
                    { label: 'C.', sortField: '_createdAt', disableSort, hide: !showDates.createdAt },
                    { label: 'U.', sortField: '_updatedAt', disableSort, hide: !showDates.updatedAt },
                ],
                optionalRender: DisplayDates,
                className: 'global-task-list__column-date',
                render: ({ _createdAt = 0, startDate = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                    <FormatMultipleDate
                        dates={[
                            {
                                label: 'S.',
                                value: startDate,
                                onClick: (): void => filterDate(startDate, 'startDate'),
                                hide: !showDates.startDate,
                            },
                            {
                                label: 'C.',
                                value: _createdAt,
                                onClick: (): void => filterDate(_createdAt, '_createdAt'),
                                hide: !showDates.createdAt,
                            },
                            {
                                label: 'U.',
                                value: _updatedAt,
                                onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                                hide: !showDates.updatedAt,
                            },
                        ]}
                    />
                ),
            },
            {
                className: 'global-task-list__column-actions',
                render: (task): ReactElement | null =>
                    !!extractPermissions(task).includes(OrgPermissionsEnum.MANAGE_TASK as never) ? (
                        <ActionsList
                            actionList={actionList(
                                i,
                                history,
                                task.extraData?.case?._id || '',
                                pinContext,
                            )([task], (count) => {
                                listRefetch();
                                count && countRefetch();
                            })}
                            isIcon
                            scope="single"
                        />
                    ) : null,
            },
        ]);

    const quickFilterList = quickFilterListRenderer([
        {
            title: i('filter.quick.task.my'),
            onClick: ({ assignee }): void => assignee(login),
        },
        { divider: true },
        {
            title: i('filter.quick.task.in-progress'),
            onClick: ({ status }): void => status(TaskStatus.IN_PROGRESS),
        },
        {
            title: i('filter.quick.task.waiting'),
            onClick: ({ status }): void => status(TaskStatus.WAITING),
        },
        {
            title: i('filter.quick.task.completed'),
            onClick: ({ status }): void => status(TaskStatus.COMPLETED),
        },
        { divider: true },
        {
            title: i('filter.quick.task.flagged'),
            onClick: ({ flag }): void => flag(),
        },
    ]);

    const { data: describe } = useListAllFields();

    const taskList = useMemo(
        () =>
            localListAsGroup
                ? dataSource.reduce((acc, curr) => {
                      const currentGroup = acc[curr.group] || [];

                      return { ...acc, [curr.group]: [...currentGroup, curr] };
                  }, {} as { [key: string]: TaskOutput[] })
                : dataSource,
        [localListAsGroup, dataSource],
    );

    useEffect(() => {
        setDisplay?.('startDate', showDates.startDate);
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            startDate: showDates.startDate,
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePage, showDates, setDefaultDisplay]);

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    useEffect(() => {
        setListAsGroup?.(localListAsGroup);
    }, [localListAsGroup]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                        <QuickFilterList quickFilterList={quickFilterList} />

                        <ExportButton
                            onClick={() => {
                                setExportDrawerVisible(true);
                            }}
                            objectType="Task"
                            selectedItems={dataSource.reduce((acc: string[], data) => {
                                if (selectedKeys.includes(data['_id'])) {
                                    acc.push(data['_id']);
                                }
                                return acc;
                            }, [])}
                            queryParams={queryParams}
                            visible={exportDrawerVisible}
                            afterVisible={setExportDrawerVisible}
                            listNumber={table.count}
                        />

                        <SelectedInfo count={selectedKeys?.length} label={i('common.selected')} />
                    </THList.Slot>

                    <THList.Slot name="NAVIGATION">
                        {!localAutoRefresh && (
                            <THTooltip
                                title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                placement="bottom"
                            >
                                <THButton
                                    size="small"
                                    type={needRefresh ? 'primary' : 'default'}
                                    shape="circle"
                                    onClick={() => {
                                        listRefetch();
                                        countRefetch();
                                        setNeedRefresh(false);
                                    }}
                                >
                                    <ICONS.SyncOutlined />
                                </THButton>
                            </THTooltip>
                        )}
                        <ManageSwitch
                            label={i('common.auto-refresh')}
                            value={localAutoRefresh}
                            onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                            icon={<ICONS.SyncOutlined />}
                        />

                        <ManageSwitch
                            label={i('task.list.table.group.show-group')}
                            value={localListAsGroup}
                            onChange={(): void => {
                                setlocalListAsGroup(!localListAsGroup || false);
                            }}
                            icon={<ICONS.GroupOutlined />}
                        />

                        <ManageSwitch
                            label={i('common.filters')}
                            value={localFilterIsOpen}
                            onChange={(editFilters): void => setLocalFilters(editFilters)}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>

                    <THList.Slot name="CONTENT">
                        {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.TASK]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                {Array.isArray(taskList) ? (
                    <THList.Content
                        dataSource={taskList}
                        columns={getColumns()}
                        rowKey="_id"
                        emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('task.none')} />}
                    />
                ) : (
                    <div className="task-list__group-content">
                        {Object.entries(taskList).map(
                            ([name, data]): ReactElement => (
                                <THList.Content
                                    key={name}
                                    name={name}
                                    dataSource={data}
                                    columns={getColumns(true)}
                                    rowKey="_id"
                                    emptyPlaceholder={
                                        <DisplayEmptyData spin={status === 'loading'} label={i('task.none')} />
                                    }
                                />
                            ),
                        )}
                    </div>
                )}

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {data && visible && (
                <TaskDrawer
                    visible={visible}
                    onVisibleChange={setVisible}
                    data={data}
                    caseId={data?.extraData?.case?._id || ''}
                    eventList={eventList}
                    actionList={actionList(i, history, data.extraData?.case?._id || '', pinContext)}
                    hasPermissions={data.extraData?.case?.userPermissions?.includes(
                        OrgPermissionsEnum.MANAGE_TASK as never,
                    )}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('globalTasks.default');

const MenuLink = (): ReactElement => {
    const local = useLocalMessage();
    return <Link to={path}>{local('case.layout.nav-bar.tasks')}</Link>;
};

const MenuIcon = () => {
    const { data: describeData } = useListAllFields();

    const { data = 0, refetch } = useGenericCountQuery({
        queryParams: {
            selector: {
                head: [{ name: 'listCaseTask' }],
                name: `count-menu-get-global-tasks`,
                initialFilters: [
                    {
                        field: 'status',
                        operator: 'any',
                        values: [TaskStatus.WAITING],
                        type: 'string',
                        describeAttribute: describeData?.task.attributes.find(({ name }) => name === 'status') as any,
                    },
                ],
            },
        },
        controller: client.TaskController,
        fetchOnStart: true,
    });

    useStreamObserver({ objectType: STREAM_EVENTS.TASK }, (data): void => {
        const needUpdate = data.some(({ base }) => {
            const { action, details } = base || {};
            return (
                ['create', 'delete'].includes(action) ||
                (action === 'update' && Object.keys(details).includes('status'))
            );
        });

        if (needUpdate) {
            refetch();
        }
    });

    return (
        <IconCount count={data}>
            <ICONS.TaskIcon />
        </IconCount>
    );
};

export const Route: RouteType = {
    menu: {
        key: 'globalTasks',
        icon: <MenuIcon />,
        render: <MenuLink />,
        selectTab: path,
    },
    path,
    content: <GlobalTaskList />,
    exact: true,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.tasks' },
};
